import { useState } from "react";
import { makeStyles } from "tss-react/mui";
import { BUTTON_VARIANTS } from "@miplatform/constants";
import { handleSearch as handleSearchFilter } from "@miplatform/utils";
import {
  mdiArrowDown,
  mdiArrowUp,
  mdiChevronDoubleLeft,
  mdiChevronDoubleRight,
  mdiChevronLeft,
  mdiChevronRight,
  mdiMagnify,
} from "@mdi/js";
import {
  Checkbox,
  GridLegacy,
  InputBase,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Typography,
} from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiIcon } from "../MiIcon/MiIcon";

const useStyles = makeStyles()((theme) => ({
  root: {
    margin: "auto",
  },
  list: {
    width: 380,
    height: 330,
    marginTop: 15,
    overflow: "auto",
  },
  button: {
    margin: 5,
    color: theme.palette.primary.main,
  },
  heading: {
    alignItems: "center",
    justifyContent: "center",
    display: "flex",
    backgroundColor: theme.palette.grey.lightAccent,
    height: 35,
    fontWeight: theme.fontWeight.light,
  },
  search: {
    display: "flex",
    position: "relative",
    borderTop: `1px solid ${theme.palette.grey.accent}`,
    borderBottom: `1px solid ${theme.palette.grey.accent}`,
    marginRight: theme.spacing(2),
    marginLeft: 0,
    width: "100%",
    [theme.breakpoints.up("sm")]: {
      marginLeft: theme.spacing(3),
      width: "auto",
    },
  },
  searchIcon: {
    padding: `${theme.spacing(0, 3)}rem`,
    height: "100%",
    pointerEvents: "none",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: theme.palette.common.black,
  },
  inputRoot: {
    color: "inherit",
    width: "100%",
  },
  inputInput: {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1rem + ${theme.spacing(8)}rem)`,
    transition: theme.transitions.create("width"),
    width: "100%",
    [theme.breakpoints.up("md")]: {
      width: "20ch",
    },
    color: theme.palette.common.black,
    fontSize: theme.fontSize.medium,
  },
}));

function not(a, b) {
  return a.filter((value) => b.indexOf(value) === -1);
}

function intersection(a, b) {
  return a?.filter((value) => b.indexOf(value) !== -1);
}

function moveUp(listItemsRight, rightchecked) {
  const elementIndex = listItemsRight.indexOf(rightchecked[0]);
  listItemsRight.splice(elementIndex, 1);
  listItemsRight.splice(elementIndex - 1, 0, rightchecked[0]);
  return [...listItemsRight];
}

function moveDown(listItemsRight, rightchecked) {
  const elementIndex = listItemsRight.indexOf(rightchecked[0]);
  listItemsRight.splice(elementIndex, 1);
  listItemsRight.splice(elementIndex + 1, 0, rightchecked[0]);
  return [...listItemsRight];
}

/**
 * A transfer list component that allows items to be moved between two lists with support for searching,
 * selecting, and reordering items.
 *
 * @component
 * @example
 * const leftItems = [
 *   { name: 'Item 1' },
 *   { name: 'Item 2' }
 * ];
 * const rightItems = [
 *   { name: 'Item A' },
 *   { name: 'Item B' }
 * ];
 *
 * return (
 *   <MiTransferList
 *     headerLeft="Left List"
 *     headerRight="Right List"
 *     listItemsLeft={leftItems}
 *     listItemsRight={rightItems}
 *     rightHandler={(items) => console.log('Right Items:', items)}
 *     leftHandler={(items) => console.log('Left Items:', items)}
 *   />
 * );
 *
 * @param {Object} props - The component props
 * @param {string} [props.headerLeft] - The header text for the left list
 * @param {string} [props.headerRight] - The header text for the right list
 * @param {Array} props.listItemsLeft - Array of items to display in the left list
 * @param {Array} props.listItemsRight - Array of items to display in the right list
 * @param {Object} [props.headingStyles] - Custom styles for the list heading
 * @param {Object} [props.listStyles] - Custom styles for the list container
 * @param {Object} [props.buttonStyles] - Custom styles for the buttons
 * @param {Function} props.rightHandler - Callback function for updating the right list
 * @param {Function} props.leftHandler - Callback function for updating the left list
 * @param {boolean} [props.readOnly] - If true, the lists are in read-only mode
 * @param {string} [props.labelField='name'] - The field to use for the primary label in list items
 * @param {string} [props.secondLabelField] - Optional field for a secondary label in list items
 * @param {string} [props.rightLabelField] - Optional field for a label displayed on the right side in list items
 * @param {Function} [props.onClick] - Optional click handler for the entire component
 * @param {boolean} [props.showUpDown=false] - If true, show the up and down move buttons for the right list
 *
 * @returns {JSX.Element} The rendered component
 */

export function MiTransferList({
  headerLeft,
  headerRight,
  listItemsLeft,
  listItemsRight,
  headingStyles,
  listStyles,
  buttonStyles,
  rightHandler,
  leftHandler,
  readOnly,
  labelField = "name",
  secondLabelField,
  rightLabelField,
  onClick,
  showUpDown,
}) {
  const { classes, theme } = useStyles();
  const [checked, setChecked] = useState([]);
  const [leftSearchText, setLeftSearchText] = useState("");
  const [rightSearchText, setRightSearchText] = useState("");

  const leftChecked = intersection(checked, listItemsLeft);
  const rightChecked = intersection(checked, listItemsRight);
  const handleToggle = (value) => () => {
    const currentIndex = checked.indexOf(value);

    const newChecked = [...checked];

    if (currentIndex === -1) {
      newChecked.push(value);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    setChecked(newChecked);
  };

  const handleAllRight = () => {
    rightHandler(listItemsRight.concat(listItemsLeft));
    leftHandler([]);
  };

  const handleCheckedRight = () => {
    rightHandler(listItemsRight.concat(leftChecked));
    leftHandler(not(listItemsLeft, leftChecked));
    setChecked(not(checked, leftChecked));
  };

  const handleCheckedLeft = () => {
    leftHandler(listItemsLeft.concat(rightChecked));
    rightHandler(not(listItemsRight, rightChecked));
    setChecked(not(checked, rightChecked));
  };

  const handleAllLeft = () => {
    leftHandler(listItemsLeft.concat(listItemsRight));
    rightHandler([]);
  };
  const handleMoveUp = () => {
    if (listItemsRight.indexOf(rightChecked[0]) !== 0) {
      rightHandler(moveUp(listItemsRight, rightChecked));
    }
  };
  const handleMoveDown = () => {
    if (listItemsRight.indexOf(rightChecked[0]) !== listItemsRight.length - 1) {
      rightHandler(moveDown(listItemsRight, rightChecked));
    }
  };

  const handleSearch = (searchText, heading) => {
    if (heading === headerLeft) {
      setLeftSearchText(searchText);
    } else {
      setRightSearchText(searchText);
    }
  };

  const handleFilter = (arr, heading) => {
    const searchText =
      heading === headerLeft ? leftSearchText : rightSearchText;

    return handleSearchFilter(arr, searchText);
  };

  const customList = (items, heading) => (
    <Paper className={classes.list} style={{ ...listStyles }}>
      <Typography className={classes.heading} style={{ ...headingStyles }}>
        {heading || "LIST ITEMS"}
      </Typography>
      <div className={classes.search}>
        <div className={classes.searchIcon}>
          <MiIcon path={mdiMagnify} />
        </div>

        <InputBase
          id={heading}
          placeholder="Search"
          classes={{
            root: classes.inputRoot,
            input: classes.inputInput,
          }}
          onChange={(e) => handleSearch(e.target.value, heading)}
          inputProps={{ "aria-label": "search" }}
        />
      </div>

      <List dense component="div" role="list">
        {items?.map((value, index) => {
          const labelId = `transfer-list-item-${value[labelField]}${
            index + heading
          }-label`;

          return (
            <ListItem
              data-testid="list-item"
              key={labelId}
              role="listitem"
              button
              disabled={value?.disable || readOnly}
              onClick={handleToggle(value)}
            >
              <ListItemIcon>
                <Checkbox
                  checked={checked.indexOf(value) !== -1 && !readOnly}
                  tabIndex={-1}
                  disableRipple
                  inputProps={{ "aria-labelledby": labelId }}
                />
              </ListItemIcon>
              <GridLegacy>
                <ListItemText
                  primaryTypographyProps={{
                    fontSize: theme.fontSize.small,
                    lineHeight: 1,
                  }}
                  id={labelId}
                  primary={value.text || value[labelField]}
                />
                {secondLabelField && (
                  <ListItemText
                    primaryTypographyProps={{ fontSize: theme.fontSize.small }}
                    sx={{
                      color: theme.palette.grey.main,
                    }}
                    id={labelId}
                    primary={value[secondLabelField]}
                  />
                )}
              </GridLegacy>
              {rightLabelField && (
                <ListItemText
                  sx={{ textAlign: "right" }}
                  primaryTypographyProps={{ fontSize: theme.fontSize.small }}
                  id={labelId}
                  primary={value[rightLabelField]}
                />
              )}
            </ListItem>
          );
        })}
      </List>
    </Paper>
  );

  return (
    <GridLegacy
      container
      spacing={2}
      justifyContent="flex-start"
      alignItems="center"
      onClick={onClick}
      className={classes.root}
    >
      <GridLegacy item>
        {customList(handleFilter(listItemsLeft, headerLeft), headerLeft)}
      </GridLegacy>
      <GridLegacy item>
        <GridLegacy container direction="column" alignItems="center">
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            startIcon={{
              path: mdiChevronDoubleRight,
            }}
            size="large"
            onClick={handleAllRight}
            disabled={listItemsLeft?.length === 0}
            aria-label="move all listItemsRight"
            data-testid="right-button"
          />
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            size="large"
            startIcon={{
              path: mdiChevronRight,
            }}
            className={classes.button}
            onClick={handleCheckedRight}
            disabled={leftChecked?.length === 0}
            aria-label="move selected listItemsRight"
          />
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            size="large"
            startIcon={{
              path: mdiChevronLeft,
            }}
            className={classes.button}
            onClick={handleCheckedLeft}
            disabled={rightChecked?.length === 0}
            aria-label="move selected listItemsLeft"
          />
          <MiButton
            variant={BUTTON_VARIANTS.OUTLINED}
            size="large"
            startIcon={{
              path: mdiChevronDoubleLeft,
            }}
            className={classes.button}
            onClick={handleAllLeft}
            disabled={listItemsRight.length === 0}
            aria-label="move all listItemsLeft"
          />
        </GridLegacy>
      </GridLegacy>
      <GridLegacy item>
        {customList(handleFilter(listItemsRight, headerRight), headerRight)}
      </GridLegacy>
      {showUpDown && (
        <GridLegacy item>
          <GridLegacy container direction="column" alignItems="center">
            <MiButton
              variant={BUTTON_VARIANTS.OUTLINED}
              size="large"
              startIcon={{
                path: mdiArrowUp,
              }}
              className={classes.button}
              onClick={handleMoveUp}
              disabled={rightChecked?.length !== 1}
              aria-label="move selected up"
              style={{ ...buttonStyles }}
            />
            <MiButton
              variant={BUTTON_VARIANTS.OUTLINED}
              size="large"
              startIcon={{
                path: mdiArrowDown,
              }}
              className={classes.button}
              onClick={handleMoveDown}
              disabled={rightChecked?.length !== 1}
              aria-label="move selected down"
              style={{ ...buttonStyles }}
            />
          </GridLegacy>
        </GridLegacy>
      )}
    </GridLegacy>
  );
}
